import { Button, SpaceBetween, Spinner } from "@cloudscape-design/components";
import { renderPowerBy } from "../../../../components/PowerBy";
import { renderProviderLogo } from "../../../../components/ProviderLogo";
import { TextInput } from "../../../../components/TextInput";
import { DropdownBox } from "../../../../components/DropdownBox";
import PhoneInput from 'react-phone-input-2'
import { Multiselect } from "../../../../components/Multiselect";
import { contactOptions } from "../../QuestionPages/Questions/questionOptions";

// Custom questions
import renderBPcuffQuestion from "../questions/QuestionBloodPressureCuff";
import renderGlucometerQuestion from "../questions/QuestionGlucometer";
import renderAddressQuestion from "../questions/QuestionAddress";

// API Response
import { ValidProviderAPIResponse } from "../types";

// Static contact component for support contact
import { support_contact } from "../../../../res/support"


type Props = {
    provider: string | undefined;
    validProvider: boolean;
    showRegistrationForm: boolean;
    apiResponse: ValidProviderAPIResponse | null; // define this type appropriately

    selectedLanguage: string;
    setSelectedLanguage: (value: string) => void;
    validSelectedLanguage: boolean;

    selectedPrograms: string[];
    setSelectedPrograms: (value: string[]) => void;
    validSelectedPrograms: boolean

    firstNameValue: string;
    setFirstNameValue: (value: string) => void;
    validFirstName: boolean;

    lastNameValue: string;
    setLastNameValue: (value: string) => void;
    validLastName: boolean;

    phoneValue: string;
    setPhoneValue: (value: string) => void;
    validPhoneNumber: boolean;

    emailValue: string;
    setEmailValue: (value: string) => void;
    validEmail: boolean;

    preferredContactMethodValue: string[];
    setPreferredContactMethod: (value: string[]) => void;
    validPreferredContactMethod: boolean;

    bpCuffValue: string;
    setBpCuffValue: (value: string) => void;
    validBpCuff: boolean;

    glucometerValue: string;
    setGlucometerValue: (value: string) => void;
    validGlucometer: boolean;

    addressStreetValue: string;
    setAddressStreetValue: (value: string) => void;
    validAddressStreet: boolean;
    addressCityValue: string;
    setAddressCityValue: (value: string) => void;
    validAddressCity: boolean;
    addressStateValue: string;
    setAddressStateValue: (value: string) => void;
    validAddressState: boolean;
    addressZipCodeValue: string;
    setAddressZipCodeValue: (value: string) => void;
    validAddressZipCode: boolean;

    statusMessage: JSX.Element | undefined;
    submit: () => void;
    submissionInProgress: boolean;
};

function RegistrationFormMultiProgram(props: Props) {
    const {
        provider,
        validProvider,
        showRegistrationForm,
        apiResponse,
        selectedLanguage,
        setSelectedLanguage,
        validSelectedLanguage,
        selectedPrograms,
        setSelectedPrograms,
        validSelectedPrograms,
        firstNameValue,
        setFirstNameValue,
        validFirstName,
        lastNameValue,
        setLastNameValue,
        validLastName,
        phoneValue,
        setPhoneValue,
        validPhoneNumber,
        emailValue,
        setEmailValue,
        validEmail,
        preferredContactMethodValue,
        setPreferredContactMethod,
        validPreferredContactMethod,
        bpCuffValue,
        setBpCuffValue,
        validBpCuff,
        glucometerValue,
        setGlucometerValue,
        validGlucometer,
        addressStreetValue,
        setAddressStreetValue,
        validAddressStreet,
        addressCityValue,
        setAddressCityValue,
        validAddressCity,
        addressStateValue,
        setAddressStateValue,
        validAddressState,
        addressZipCodeValue,
        setAddressZipCodeValue,
        validAddressZipCode,
        statusMessage,
        submit,
        submissionInProgress
    } = props;

    return (
        <div className="center-screen">
            <div className="glow-border page-container" style={{ maxWidth: "330px" }}>
                <SpaceBetween size='m' direction='vertical'>

                    {/* Optional logo from provider */}
                    {provider ? renderProviderLogo({ provider }) : null}

                    <h3 className='line-align-center' style={{ marginBottom: '0' }}>Program Enrollment</h3>

                    {/* Invalid program/provider message */}
                    {(!validProvider) &&
                        <div>
                            <p>This is not a valid registration link.</p>
                            {support_contact}
                        </div>
                    }

                    {/* Registration form */}
                    {(showRegistrationForm) && (apiResponse) &&
                        <SpaceBetween size='m' direction='vertical'>
                            <DropdownBox
                                label="Select a Language"
                                options={apiResponse.supported_languages.map(language => ({ value: language.language_id, label: language.language_name }))}
                                selectedValue={selectedLanguage}
                                onChange={setSelectedLanguage}
                                showMessage={!validSelectedLanguage}
                            />

                            <Multiselect
                                label="Select Programs"
                                options={apiResponse.programs.map(program => ({ value: program.program, label: `${program.name} (${program.program})` }))}
                                selectedValue={selectedPrograms}
                                onChange={setSelectedPrograms}
                                showMessage={!validSelectedPrograms}
                            />

                            <SpaceBetween size='xxxs' direction='vertical'>
                                <div>Name</div>
                                <SpaceBetween size='xxxs' direction='horizontal'>
                                    <TextInput
                                        value={firstNameValue}
                                        onChange={setFirstNameValue}
                                        placeholder="First Name"
                                        showMessage={!validFirstName}
                                    />
                                    <TextInput
                                        value={lastNameValue}
                                        onChange={setLastNameValue}
                                        placeholder="Last Name"
                                        showMessage={!validLastName}
                                    />
                                </SpaceBetween>
                            </SpaceBetween>

                            <SpaceBetween size='m' direction='horizontal'>
                                <div>Phone Number</div>
                            </SpaceBetween>

                            <PhoneInput
                                country={'us'}
                                value={phoneValue}
                                onChange={setPhoneValue}
                                buttonStyle={{
                                    borderTopLeftRadius: '8px',
                                    borderBottomLeftRadius: '8px',
                                    borderTop: '2px solid #9ba7b6',
                                    borderBottom: '2px solid #9ba7b6',
                                    borderLeft: '2px solid #9ba7b6'
                                }}
                                inputStyle={{
                                    width: '100%',
                                    borderTopRightRadius: '8px',
                                    borderBottomRightRadius: '8px',
                                    borderTop: '2px solid #9ba7b6',
                                    borderBottom: '2px solid #9ba7b6',
                                    borderRight: '2px solid #9ba7b6'
                                }}
                                placeholder="Phone number"
                            />
                            {!validPhoneNumber && <p className='tip-text'>Please enter a valid phone number.</p>}

                            <TextInput
                                label="Email"
                                value={emailValue}
                                onChange={setEmailValue}
                                type="email"
                                placeholder="Email Address"
                                showMessage={!validEmail}
                                message="Please enter a valid email."
                            />

                            <Multiselect
                                label="Which contact methods do you prefer? (Select all that apply)"
                                options={contactOptions}
                                selectedValue={preferredContactMethodValue}
                                onChange={setPreferredContactMethod}
                                showMessage={!validPreferredContactMethod}
                            />

                            {renderBPcuffQuestion(
                                apiResponse.show_bp_cuff_question && selectedPrograms.includes("HTN"), // show_bp_cuff_question - depends on program selection
                                apiResponse.provide_bp_cuff_option,
                                apiResponse.bp_cuff_require_bluetooth_option,
                                bpCuffValue,
                                setBpCuffValue,
                                validBpCuff
                            )}

                            {renderGlucometerQuestion(
                                apiResponse.show_glucometer_question &&
                                (selectedPrograms.includes("DM") || selectedPrograms.includes("DM_HYBRID")), // show_glucometer_question - depends on program selection
                                apiResponse.provide_glucometer_option,
                                !selectedPrograms.includes("DM_HYBRID"), // finger_prick_option - depends on program selection
                                glucometerValue,
                                setGlucometerValue,
                                validGlucometer
                            )}

                            {renderAddressQuestion(
                                bpCuffValue,
                                glucometerValue,
                                addressStreetValue,
                                setAddressStreetValue,
                                validAddressStreet,
                                addressCityValue,
                                setAddressCityValue,
                                validAddressCity,
                                addressStateValue,
                                setAddressStateValue,
                                validAddressState,
                                addressZipCodeValue,
                                setAddressZipCodeValue,
                                validAddressZipCode
                            )}

                            <div className='line-align-center'>
                                <Button variant='primary' onClick={submit} disabled={submissionInProgress}>
                                    {submissionInProgress && <Spinner />}
                                    Submit
                                </Button>
                            </div>

                            <p className='tip-text' style={{ fontSize: '12px', textAlign: 'center', marginTop: '8px', color: 'black' }}>
                                By providing your phone number and opting in, you agree to receive SMS messages from CIPRA.ai related to your remote patient monitoring program, including reminders and care updates. Message frequency varies. Message and data rates may apply. Reply STOP to cancel or HELP for help.
                            </p>
                        </SpaceBetween>
                    }

                    {/* Status message after submission*/}
                    {statusMessage && <p className='tip-text'>{statusMessage}</p>}

                    {/* Branding footer */}
                    {provider ? renderPowerBy({ provider: provider?.toUpperCase() }) : null}
                </SpaceBetween>
            </div>
        </div>
    );
}

export default RegistrationFormMultiProgram;